import React, { useEffect, useMemo, useState } from 'react'
import Chart from 'react-apexcharts'
import { ApexOptions } from 'apexcharts'
import { AppSelect as Select } from '../../../../../../components/Select'
import InfoHint from '../../../../../common/InfoHint'
import { getMerchantLookup, getPaymentMethodLookup } from '../../../../../common/core/_requests'
import { METRIC_HINTS } from '../../Dashboard/core/hints'
import { useChartTheme } from '../../Dashboard/core/chartTheme'
import { dayDividerEvents, hourlyAxis } from '../../Dashboard/core/timeAxis'
import { getPayoutPoolFill } from '../core/api'
import { POOL_FILL_CRIT, POOL_FILL_WARN, POOL_FILL_ZONE_COLORS } from '../core/consts'
import { buildPoolFillSeries } from '../core/poolFillSeries'
import { buildPoolFillTooltipHtml } from '../core/poolFillTooltip'
import { PoolFillPoint } from '../core/types'

type Option = { value: number; label: string }
type Props = { currency: string; refreshToken: number; onLoaded: (currencies: string[]) => void }

const HEIGHT = 340

// Заполненность пула активных выплат за 72 часа, шаг 15 минут. Фильтры «Мерчант» и
// «Платёжный метод» сужают пул; период фиксирован.
const PayoutPoolFillChart: React.FC<Props> = ({ currency, refreshToken, onLoaded }) => {
  const [points, setPoints] = useState<PoolFillPoint[]>([])
  const [merchant, setMerchant] = useState<Option | null>(null)
  const [paymentMethod, setPaymentMethod] = useState<Option | null>(null)
  const [merchantOptions, setMerchantOptions] = useState<Option[]>([])
  const [methodOptions, setMethodOptions] = useState<Option[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const theme = useChartTheme()

  // Варианты фильтров — из общих лукапов; их роли покрывают роли страницы.
  useEffect(() => {
    let cancelled = false

    getMerchantLookup()
      .then((resp) => {
        if (cancelled || !resp.data?.success) return
        setMerchantOptions(resp.data.data ?? [])
      })
      .catch(() => setMerchantOptions([]))

    getPaymentMethodLookup()
      .then((resp) => {
        if (cancelled || !resp.data?.success) return
        setMethodOptions(resp.data.data ?? [])
      })
      .catch(() => setMethodOptions([]))

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    getPayoutPoolFill(currency, { merchant: merchant?.value, paymentMethod: paymentMethod?.value })
      .then((resp) => {
        if (cancelled) return
        if (resp.data.success && resp.data.data) {
          setPoints(resp.data.data.points ?? [])
          setError(null)
          onLoaded(resp.data.data.currencies ?? [])
        } else {
          setError('Не удалось загрузить график')
          onLoaded([])
        }
      })
      .catch(() => {
        if (cancelled) return
        setError('Сервис мэтчинга недоступен')
        onLoaded([])
      })
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [currency, merchant, paymentMethod, refreshToken, onLoaded])

  // Всё, что уезжает в <Chart>, мемоизировано и меняется только вместе с данными:
  // обёртка Apex на каждый новый объект опций делает полный updateOptions.
  const axis = useMemo(() => hourlyAxis(points.map((p) => p.ts)), [points])
  const s = useMemo(() => buildPoolFillSeries(points), [points])
  const hasData = useMemo(() => s.used.some((v) => v !== null), [s])
  const colors = POOL_FILL_ZONE_COLORS[theme.mode]

  const series = useMemo(
    () => [
      { name: 'занято ≥ 80 %', type: 'area', data: s.red },
      { name: 'занято 60–80 %', type: 'area', data: s.yellow },
      { name: 'занято < 60 %', type: 'area', data: s.green },
      { name: 'занято, %', type: 'line', data: s.used },
      { name: 'без диспутов, %', type: 'line', data: s.withoutDisputes },
    ],
    [s]
  )

  const options = useMemo<ApexOptions>(
    () => ({
      chart: {
        type: 'line',
        toolbar: { show: false },
        zoom: { enabled: false },
        fontFamily: 'inherit',
        background: 'transparent',
        animations: { enabled: false },
        redrawOnParentResize: false,
        events: dayDividerEvents(axis.dayStarts, theme.dividerColor),
      },
      theme: { mode: theme.mode },
      colors: [colors.red, colors.yellow, colors.green, colors.line, colors.line],
      fill: { type: 'solid', opacity: [1, 1, 1, 1, 1] },
      stroke: { curve: 'straight', width: [0, 0, 0, 2, 2], dashArray: [0, 0, 0, 0, 5] },
      dataLabels: { enabled: false },
      markers: { size: 0, hover: { size: 3 } },
      grid: { borderColor: theme.gridColor },
      xaxis: {
        type: 'category',
        categories: axis.categories,
        tickAmount: 12,
        labels: { rotate: 0, hideOverlappingLabels: true, style: { fontSize: '10px' } },
        // Тултип оси у Apex полупрозрачный и наезжает на подпись; время есть в основном.
        tooltip: { enabled: false },
      },
      yaxis: {
        min: 0,
        max: 100,
        tickAmount: 5,
        title: { text: '% занятой ёмкости' },
        labels: { formatter: (v: number) => `${Math.round(v)}%` },
      },
      annotations: {
        yaxis: [POOL_FILL_WARN, POOL_FILL_CRIT].map((y) => ({
          y,
          borderColor: theme.dividerColor,
          strokeDashArray: 3,
        })),
      },
      legend: { show: true, position: 'top', horizontalAlign: 'left', markers: { shape: 'square' } },
      tooltip: {
        shared: true,
        intersect: false,
        custom: ({ dataPointIndex }: { dataPointIndex: number }) =>
          points[dataPointIndex] ? buildPoolFillTooltipHtml(points[dataPointIndex], currency) : '',
      },
    }),
    [axis, colors, currency, points, theme]
  )

  // Селекты живут в шапке карточки; ширину задаём явно, иначе react-select
  // схлопывается по длине плейсхолдера.
  const selectStyles = {
    container: (base: Record<string, unknown>) => ({ ...base, minWidth: 190 }),
    menu: (base: Record<string, unknown>) => ({ ...base, zIndex: 5 }),
  }

  return (
    <div className='card mb-5'>
      <div className='card-header d-flex flex-wrap justify-content-between align-items-center gap-3'>
        <h3 className='card-title d-inline-flex align-items-center'>
          Заполненность пула активных выплат
          <InfoHint text={METRIC_HINTS.payout_pool_fill} />
        </h3>
        <div className='d-flex flex-wrap align-items-center gap-3'>
          <Select<Option>
            className='form-select-sm p-0'
            classNamePrefix='select'
            styles={selectStyles}
            options={merchantOptions}
            value={merchant}
            onChange={(o) => setMerchant(o)}
            isClearable
            placeholder='Мерчант: все'
            noOptionsMessage={() => 'Нет вариантов'}
          />
          <Select<Option>
            className='form-select-sm p-0'
            classNamePrefix='select'
            styles={selectStyles}
            options={methodOptions}
            value={paymentMethod}
            onChange={(o) => setPaymentMethod(o)}
            isClearable
            placeholder='Платёжный метод: все'
            noOptionsMessage={() => 'Нет вариантов'}
          />
        </div>
      </div>
      {/* Высота тела зафиксирована: смена состояния не двигает страницу под курсором. */}
      <div className='card-body' style={{ minHeight: HEIGHT }}>
        {loading && <div className='text-muted'>Загрузка…</div>}
        {error && <div className='alert alert-danger'>{error}</div>}
        {!loading &&
          !error &&
          (hasData ? (
            <Chart options={options} series={series} type='line' height={HEIGHT} />
          ) : (
            <div className='text-muted'>
              За последние 3 дня по этому срезу активных выплат не было.
            </div>
          ))}
      </div>
    </div>
  )
}

export default PayoutPoolFillChart
